'use client'

import { useState, useEffect } from 'react'
import { User } from '@/types'
import { useUser } from '@/hooks/useUser'
import { useToast } from '@/hooks/useToast'
import { getApiUrl } from '@/lib/apiUrl'



interface AdminNotification {
  id: string
  admin_id: string
  type: 'user_verification' | 'property_review' | 'dispute' | 'payment' | 'system' | 'custom'
  title: string
  message: string
  priority: 'low' | 'medium' | 'high' | 'urgent'
  status: 'unread' | 'read' | 'archived'
  action_required: boolean
  action_url?: string
  created_at: string
  read_at?: string
  admin?: User
}

interface AdminNotificationSystemProps {
  onUpdate: () => void
}

export default function AdminNotificationSystem({ onUpdate }: AdminNotificationSystemProps) {
  const { token } = useUser()
  const toast = useToast()
  const [notifications, setNotifications] = useState<AdminNotification[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<'all' | 'unread' | 'read' | 'archived'>('all')
  const [priorityFilter, setPriorityFilter] = useState<'all' | 'low' | 'medium' | 'high' | 'urgent'>('all')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [newNotification, setNewNotification] = useState({
    type: 'custom' as const,
    title: '',
    message: '',
    priority: 'medium' as const,
    action_required: false,
    action_url: '',
    target: 'admins' as 'admins' | 'all_users'
  })

  // Load notifications
  const loadNotifications = async () => {
    setLoading(true)
    try {
      if (!token) {
        setNotifications([])
        return
      }

      const apiUrl = getApiUrl()
      const params = new URLSearchParams()
      params.set('status', filter)
      params.set('priority', priorityFilter)

      const response = await fetch(`${apiUrl}/admin/notifications?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      const payload = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error(payload?.message || 'Failed to load notifications')
      }

      setNotifications(payload?.notifications || [])
    } catch (error) {
      console.error('Error:', error)
      toast.error(error instanceof Error ? error.message : 'Failed to load notifications')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadNotifications()
  }, [])

  // Mark notification as read
  const markAsRead = async (_notificationId: string) => {
    try {
      if (!token) return
      const apiUrl = getApiUrl()
      await fetch(`${apiUrl}/admin/notifications/${_notificationId}/read`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      })

      await loadNotifications()
      onUpdate()
    } catch (error) {
      console.error('Error:', error)
    }
  }

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      if (!token) return
      const apiUrl = getApiUrl()
      await fetch(`${apiUrl}/admin/notifications/read-all`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      })

      await loadNotifications()
      onUpdate()
      toast.success('All notifications marked as read')
    } catch (error) {
      console.error('Error:', error)
    }
  }

  // Archive notification
  const archiveNotification = async (_notificationId: string) => {
    try {
      if (!token) return
      const apiUrl = getApiUrl()
      await fetch(`${apiUrl}/admin/notifications/${_notificationId}/archive`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      })

      await loadNotifications()
      onUpdate()
    } catch (error) {
      console.error('Error:', error)
    }
  }

  // Create custom notification
  const createNotification = async () => {
    if (!newNotification.title || !newNotification.message) {
      toast.error('Please fill in all required fields')
      return
    }

    try {
      if (!token) return
      const apiUrl = getApiUrl()
      const response = await fetch(`${apiUrl}/admin/notifications`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          type: newNotification.type,
          title: newNotification.title,
          message: newNotification.message,
          priority: newNotification.priority,
          action_required: newNotification.action_required,
          action_url: newNotification.action_url || undefined,
          broadcast: true,
          target: newNotification.target
        })
      })

      const payload = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error(payload?.message || 'Failed to create notification')
      }
      
      setShowCreateModal(false)
      setNewNotification({
        type: 'custom',
        title: '',
        message: '',
        priority: 'medium',
        action_required: false,
        action_url: '',
        target: 'admins'
      })
      await loadNotifications()
      onUpdate()
      toast.success('Notification created successfully!')
    } catch (error) {
      console.error('Error:', error)
      toast.error(error instanceof Error ? error.message : 'Failed to create notification')
    }
  }

  // Get priority color
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'bg-red-100 text-red-800'
      case 'high':
        return 'bg-orange-100 text-orange-800'
      case 'medium':
        return 'bg-yellow-100 text-yellow-800'
      case 'low':
        return 'bg-green-100 text-green-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  // Get type color
  const getTypeColor = (type: string) => {
    switch (type) {
      case 'user_verification':
        return 'bg-blue-100 text-blue-800'
      case 'property_review':
        return 'bg-green-100 text-green-800'
      case 'dispute':
        return 'bg-red-100 text-red-800'
      case 'payment':
        return 'bg-purple-100 text-purple-800'
      case 'system':
        return 'bg-gray-100 text-gray-800'
      case 'custom':
        return 'bg-indigo-100 text-indigo-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  // Filter notifications
  const filteredNotifications = notifications.filter(notification => {
    if (filter !== 'all' && notification.status !== filter) return false
    if (priorityFilter !== 'all' && notification.priority !== priorityFilter) return false
    return true
  })

  // Calculate stats
  const unreadCount = notifications.filter(n => n.status === 'unread').length
  const urgentCount = notifications.filter(n => n.priority === 'urgent' && n.status === 'unread').length
  const actionRequiredCount = notifications.filter(n => n.action_required && n.status === 'unread').length

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">Admin Notifications</h2>
        <div className="flex space-x-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as any)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Notifications</option>
            <option value="unread">Unread</option>
            <option value="read">Read</option>
            <option value="archived">Archived</option>
          </select>
          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value as any)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Priorities</option>
            <option value="urgent">Urgent</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
          >
            Create Notification
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Unread</h3>
          <p className="text-3xl font-bold text-blue-600">{unreadCount}</p>
          <p className="text-sm text-gray-500">Require attention</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Urgent</h3>
          <p className="text-3xl font-bold text-red-600">{urgentCount}</p>
          <p className="text-sm text-gray-500">Immediate action needed</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Action Required</h3>
          <p className="text-3xl font-bold text-orange-600">{actionRequiredCount}</p>
          <p className="text-sm text-gray-500">Need admin action</p>
        </div>
      </div>

      {/* Quick Actions */}
      {unreadCount > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-blue-900">
                {unreadCount} unread notifications
              </h3>
              <p className="text-sm text-blue-700">
                Mark all as read to clear the notification count
              </p>
            </div>
            <button
              onClick={markAllAsRead}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
            >
              Mark All as Read
            </button>
          </div>
        </div>
      )}

      {/* Notifications List */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Notifications</h3>
        </div>
        
        {loading ? (
          <div className="p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading notifications...</p>
          </div>
        ) : filteredNotifications.length > 0 ? (
          <div className="divide-y divide-gray-200">
            {filteredNotifications.map((notification) => (
              <div key={notification.id} className={`p-6 ${notification.status === 'unread' ? 'bg-blue-50' : ''}`}>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <h4 className="font-medium text-gray-900">{notification.title}</h4>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(notification.priority)}`}>
                        {notification.priority.charAt(0).toUpperCase() + notification.priority.slice(1)}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(notification.type)}`}>
                        {notification.type.replace('_', ' ').charAt(0).toUpperCase() + notification.type.replace('_', ' ').slice(1)}
                      </span>
                      {notification.action_required && (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          Action Required
                        </span>
                      )}
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-2">{notification.message}</p>
                    
                    <div className="text-sm text-gray-500">
                      <p>Created: {new Date(notification.created_at).toLocaleString()}</p>
                      {notification.read_at && (
                        <p>Read: {new Date(notification.read_at).toLocaleString()}</p>
                      )}
                      {notification.action_url && (
                        <p>Action URL: {notification.action_url}</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex space-x-2">
                    {notification.status === 'unread' && (
                      <button
                        onClick={() => markAsRead(notification.id)}
                        className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                      >
                        Mark Read
                      </button>
                    )}
                    <button
                      onClick={() => archiveNotification(notification.id)}
                      className="bg-gray-600 text-white px-3 py-1 rounded text-sm hover:bg-gray-700"
                    >
                      Archive
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-6 text-center text-gray-500">
            <p>No notifications found</p>
            <p className="text-sm">No notifications match your current filters</p>
          </div>
        )}
      </div>

      {/* Create Notification Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Create Notification</h3>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Type
                  </label>
                  <select
                    value={newNotification.type}
                    onChange={(e) => setNewNotification({
                      ...newNotification,
                      type: e.target.value as any
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900"
                  >
                    <option value="custom">Custom</option>
                    <option value="user_verification">User Verification</option>
                    <option value="property_review">Property Review</option>
                    <option value="dispute">Dispute</option>
                    <option value="payment">Payment</option>
                    <option value="system">System</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title *
                  </label>
                  <input
                    type="text"
                    value={newNotification.title}
                    onChange={(e) => setNewNotification({
                      ...newNotification,
                      title: e.target.value
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Message *
                  </label>
                  <textarea
                    value={newNotification.message}
                    onChange={(e) => setNewNotification({
                      ...newNotification,
                      message: e.target.value
                    })}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Priority
                  </label>
                  <select
                    value={newNotification.priority}
                    onChange={(e) => setNewNotification({
                      ...newNotification,
                      priority: e.target.value as any
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Send to
                  </label>
                  <select
                    value={newNotification.target}
                    onChange={(e) => setNewNotification({
                      ...newNotification,
                      target: e.target.value as 'admins' | 'all_users'
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900"
                  >
                    <option value="admins">Admins only</option>
                    <option value="all_users">All users (clients &amp; landlords)</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">All users will see this in their dashboard notification bell.</p>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="action_required"
                    checked={newNotification.action_required}
                    onChange={(e) => setNewNotification({
                      ...newNotification,
                      action_required: e.target.checked
                    })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="action_required" className="ml-2 block text-sm text-gray-900">
                    Action Required
                  </label>
                </div>

                {newNotification.action_required && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Action URL (Optional)
                    </label>
                    <input
                      type="url"
                      value={newNotification.action_url}
                      onChange={(e) => setNewNotification({
                        ...newNotification,
                        action_url: e.target.value
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )}

                <div className="flex space-x-3 pt-4">
                  <button
                    onClick={createNotification}
                    className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors"
                  >
                    Create Notification
                  </button>
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
} 


